import Ladehinweis from '../komponenten/Ladehinweis'
import MenschKarte from '../komponenten/MenschKarte'
import Stapel from '../komponenten/Stapel'
import { datum, punkteSatz, useLaden } from '../lib/laden'
import {
  GRUENDE_TEXT,
  holeBewertungen,
  textAblehnen,
  textFreigeben,
  type Bewertung,
  pflichtGrund,
} from '../lib/warteschlangen'

/**
 * Warteschlange „Bewertungstexte" (§8.3).
 *
 * Geprüft wird allein die Prosa. Die Sterne zählen bereits — das steht hier
 * ausdrücklich, damit niemand glaubt, er entscheide über die Bewertung.
 */
export default function Bewertungen() {
  const [zustand, neuLaden] = useLaden(holeBewertungen)

  if (zustand.art !== 'da') return <Ladehinweis zustand={zustand} />

  return (
    <Stapel<Bewertung>
      titel="Bewertungstexte"
      eintraege={zustand.wert.eintraege}
      gesamt={zustand.wert.gesamt}
      schluessel={(b) => `${b.place_id}:${b.user_firebase_uid}`}
      neuLaden={neuLaden}
      gruende={GRUENDE_TEXT}
      leerText="Kein Bewertungstext wartet auf eine Entscheidung."
      freigebenText="Text freigeben"
      ablehnenText="Text ablehnen"
      zeile={(b) => (
        <>
          <span className="stapel-zeile-haupt">
            {b.title ?? b.ort_name ?? 'ohne Titel'}
          </span>
          <span className="leise">{datum(b.submitted_at)}</span>
        </>
      )}
      inhalt={(b) => <BewertungAnsicht bewertung={b} />}
      aktion={{
        freigeben: (b) => textFreigeben(b),
        ablehnen: (b, code) => textAblehnen(b, pflichtGrund(code)),
        bericht: (b, ergebnis, art) => {
          const name =
            b.mensch?.username ??
            b.mensch?.display_name ??
            `Anonym · ${b.user_firebase_uid.slice(0, 6)}`
          const was =
            art === 'frei'
              ? 'Text freigegeben'
              : 'Text abgelehnt — die Sterne bleiben'
          return `${was} · ${punkteSatz(ergebnis.punkte.differenz, name)}`
        },
      }}
    />
  )
}

function BewertungAnsicht({ bewertung: b }: { bewertung: Bewertung }) {
  return (
    <div className="ansicht">
      <div className="karte">
        <div className="sterne-zeile">
          <Sterne wert={b.rating} />
          <span className="zahl gedaempft">{b.rating.toFixed(1)}</span>
        </div>
        <p className="hinweis-sterne">
          Die Sterne zählen bereits im Durchschnitt. Hier geht es allein um
          Titel und Text — eine Ablehnung nimmt sie nicht heraus.
        </p>

        {b.title && <h2 className="bewertung-titel">{b.title}</h2>}
        <p className="bewertung-text">{b.text ?? '(kein Text)'}</p>
      </div>

      <div className="karte">
        <h3>{b.ort_name ?? 'Ort ohne Namen'}</h3>
        <p className="gedaempft">
          {[b.ort_strasse, b.ort_stadt].filter(Boolean).join(', ') ||
            'keine Adresse hinterlegt'}
        </p>
        <p className="leise ansicht-zeile">
          Eingereicht: {datum(b.submitted_at)} · Zeile: {b.status} · Text:{' '}
          {b.text_status}
        </p>
      </div>

      <div className="karte">
        <MenschKarte mensch={b.mensch} uid={b.user_firebase_uid} />
      </div>
    </div>
  )
}

function Sterne({ wert }: { wert: number }) {
  const voll = Math.round(wert)
  return (
    <span className="sterne" aria-label={`${wert.toFixed(1)} von 5 Sternen`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} className={n <= voll ? 'stern stern-voll' : 'stern'} aria-hidden="true">
          ★
        </span>
      ))}
    </span>
  )
}
